import {feed_fetchFeedByContainer_Api, getChannelsByProps_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import CustomLazyPaginationWrapper from 'modules/asyncCache/components/CustomLazyPaginationWrapper'
import React, {useContext, useEffect, useMemo, useState} from 'react'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from '../OrganizationContainerContext'
import {HeaderWithIconLine} from '../../Templates/items/ListHeader'
import EmptyHolder from '../../../../components/EmptyHolder'
import {WrapperBlockSider} from '../components/WrapperBlockSider'
import {RiSignalTowerFill} from 'react-icons/ri'
import {FaWandMagicSparkles} from 'react-icons/fa6'
import {FiChevronsRight} from 'react-icons/fi'
import {Link} from 'react-router-dom'
import {Skeleton} from 'antd'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import Pure from 'components/Pure'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {useHistory} from 'modules/navigation/useRouter'
import {scrollToRow} from '../../Missal/functions/scrollToRow'
import Translate from 'modules/local/Translate'
import {FollowButton} from 'components/Feed/FollowButton'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {GoDotFill} from 'react-icons/go'

// ─── Skeletons ────────────────────────────────────────────────────────────────

const ChannelSkeleton = () => (
    <div className="flex flex-col gap-4">
        {[0, 1, 2].map((i) => (
            <div key={i} className="p-4 rounded-xl border border-color-50 background">
                <Skeleton active paragraph={{rows: 3}} title={{width: '50%'}}/>
            </div>
        ))}
    </div>
)

// ─── Feed preview inside each channel card ───────────────────────────────────

const FeedPreviewItem = ({item}) => {
    const title = item?.content?.title
        || item?.content?.overview?.title
        || item?.content?.overview?.description
    if (!title) return null
    return (
        <Link
            to={getLinkToDetail(item?.content)}
            className="flex items-center gap-2 text-sm text-color-200 hover:text-primary no-underline group">
            <GoDotFill size={8} className="mt-1.5 flex-shrink-0 text-color-400"/>
            <span className="max-lines-2 leading-snug group-hover:underline">{title}</span>
        </Link>
    )
}

const FEED_PREVIEW_STAGGER_MS = 120

const ChannelFeedPreview = ({channelId, channelType, index = 0}) => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)

    const {handleAsyncAction} = useAsyncAction({
        apiInfo: feed_fetchFeedByContainer_Api,
        query: bindQueryParams([{id: channelId}, {prop: channelType}]),
        onSuccess: (result, data) => {
            setItems(_.get(data, 'response.data.data', []).slice(0, 4))
            setLoading(false)
        },
        onError: () => setLoading(false),
    })

    useEffect(() => {
        const timeout = setTimeout(() => {
            handleAsyncAction()
        }, index * FEED_PREVIEW_STAGGER_MS)
        return () => clearTimeout(timeout)
    }, [])

    return (
        <div className="transition-all duration-300 ease-out overflow-hidden">
            {loading ? (
                <div className="flex flex-col gap-2">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-color-100 flex-shrink-0"/>
                            <Skeleton active title={{width: `${40 + i * 20}%`}} paragraph={false} className="m-0"/>
                        </div>
                    ))}
                </div>
            ) : !items.length ? (
                <div className="h-4"/>
            ) : (
                <div className="flex flex-col gap-1.5">
                    {items.map((item, i) => (
                        <FeedPreviewItem key={i} item={item}/>
                    ))}
                </div>
            )}
        </div>
    )
}

// ─── Channel card ─────────────────────────────────────────────────────────────

const OrgChannelCard = ({item, index = 0}) => {
    const t = useTranslate()
    const id = getId(item)
    const type = getType(item)

    if (!item) return null

    return (
        <div
            className="background border border-color-50 rounded-xl shadow-sm hover:shadow-md hover:scale-2xs transition-all overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 flex items-center gap-3 border-b border-color-50 background-50">
                <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                    <RiSignalTowerFill size={18} className="text-blue-500"/>
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                        <Link
                            to={getLinkToDetail(item)}
                            className="font-bold text-base text-color-000 leading-snug no-underline hover:text-primary max-lines-1 flex-1 min-w-0">
                            {getTitle(item)}
                        </Link>
                        <div className="flex-shrink-0">
                            <FollowButton size="small" item={item} className="text-xs"/>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {Number(item?.total_followers) > 0 && (
                            <span className="text-xs text-color-300 italic tracking-wide lowercase">
                                <strong className="text-color-100 not-italic">
                                    {Number(item?.total_followers).toLocaleString()}
                                </strong>{' '}{t('followers')}
                            </span>
                        )}
                        {!!item?.selected_by_editor && (
                            <span
                                className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-blue-600 bg-blue-50 rounded-full border border-blue-100">
                                <FaWandMagicSparkles size={9}/>
                                {t("editor's choice")}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Feed preview */}
            <div className="px-4 py-3">
                <ChannelFeedPreview channelId={id} channelType={type} index={index}/>
            </div>
        </div>
    )
}

// ─── Channel tab main component ───────────────────────────────────────────────

const OrganizationChannelList = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const [id, type] = [getId(item), getType(item)]

    const apiInfo = getChannelsByProps_Api

    const pagerKey = useMemo(
        () => [apiInfo.path, type, id].join('/'),
        [id, type]
    )

    if (_.isEmpty(item)) return null

    return (
        <Wrapper className="flex flex-col gap-6 pb-8">
            <HeaderWithIconLine
                icon={<RiSignalTowerFill size={20} className="text-blue-500"/>}
                renderTitle={() => (
                    <div className="flex flex-col">
                        <span className="font-bold text-base text-color-000 uppercase tracking-wide">
                            {t('channel')}
                        </span>
                    </div>
                )}
                renderSubTitle={() => (
                    <div className="flex flex-col">
                               <span className="text-xs text-color-400 italic font-normal normal-case tracking-normal">
                            {t('channel introduction description')}
                        </span>
                    </div>
                )}
            />
            <Pure input={[id, type]}>
                <CustomLazyPaginationWrapper
                    alwaysVisible={true}
                    id={pagerKey}
                    apiInfo={apiInfo}
                    query={bindQueryParams([{id}, {type}])}
                    renderItem={(channel, index) => <OrgChannelCard key={getId(channel)} item={channel} index={index}/>}
                    renderLoading={() => <ChannelSkeleton/>}
                    renderWrap={(child, __, count) => {
                        if (!count) {
                            return (
                                <EmptyHolder
                                    className="py-6"
                                    title={t('channel')}
                                    subTitle={t('No data available')}
                                />
                            )
                        }
                        return (
                            <div className="flex flex-col gap-4">
                                {child}
                            </div>
                        )
                    }}
                />
            </Pure>
        </Wrapper>
    )
}

// ─── Sidebar variant ──────────────────────────────────────────────────────────

const renderChannelItemSider = ({isLg}) => (item, index) => (
    <Translate key={index}>
        {(t) => (
            <div className="px-3 py-2 background-50 hover:scale-2xs hover:shadow-md rounded-xl">
                <div className="mb-2 pb-2 border-b">
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-base text-color-000 tracking-wide max-lines-1 no-underline">
                        {getTitle(item)}
                    </Link>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-xs text-color-100 italic tracking-wider lowercase">
                        <strong>{item?.total_followers}</strong> {t('followers')}
                    </span>
                    {!!item?.selected_by_editor && (
                        <span
                            className="h-6 px-3 py-1 flex flex-center font-medium text-xs text-blue-500 bg-blue-100 tracking-wide rounded-full">
                            <FaWandMagicSparkles size={10} className="mr-1"/>
                            {!!isLg && t("editor's choice")}
                        </span>
                    )}
                </div>
            </div>
        )}
    </Translate>
)

export const OrganizationChannelSider = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const {isLg} = useContext(LayoutContext)
    const history = useHistory()
    const [id, type] = [getId(item), getType(item)]

    if (_.isEmpty(item)) return null

    return (
        <CustomLazyPaginationWrapper
            id={[getChannelsByProps_Api.path, type, id].join('/')}
            sample={2}
            auto={true}
            apiInfo={getChannelsByProps_Api}
            query={bindQueryParams([{id}, {type}])}
            renderItem={renderChannelItemSider({isLg})}
            renderWrap={(children, __, count) => !!count && (
                <WrapperBlockSider
                    title="channel" titleColor="text-blue-600"
                    expanded={!isLg}
                    borderLeft={isLg}
                    borderTop={!isLg}
                    icon={<RiSignalTowerFill size={18} className="text-blue-600"/>}>
                    <div className="flex flex-col gap-4">
                        {children}
                    </div>
                </WrapperBlockSider>
            )}
            renderLoadMoreButton={() => (
                <span
                    className="flex items-center justify-end gap-1 text-color-400 hover:text-color-100 cursor-pointer"
                    onClick={() => {
                        scrollToRow('contentOrg')
                        history.push(getLinkToDetail(item) + '/channels')
                    }}>
                    <span className="text-xs italic">{t('see more')}</span>
                    <FiChevronsRight size={13} className="my-auto"/>
                </span>
            )}
        />
    )
}

export default OrganizationChannelList
